import React, { useRef, useEffect, useState } from 'react';
import { Box, Button, Paper, CircularProgress } from '@mui/material';
import { Clear, Save } from '@mui/icons-material';

interface SignaturePadProps {
  width?: number;
  height?: number;
  onSave: (dataUrl: string) => void | Promise<void>;
  initialSignature?: string | null;
  onClear?: () => void;
  saving?: boolean;
}

const SignaturePad: React.FC<SignaturePadProps> = ({
  width = 400,
  height = 200,
  onSave,
  initialSignature = null,
  onClear,
  saving = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = width;
    canvas.height = height;

    // Set drawing styles
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Load initial signature if provided
    if (initialSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Maintain aspect ratio when drawing the image
        const imgAspect = img.width / img.height;
        const canvasAspect = canvas.width / canvas.height;
        let drawWidth = canvas.width;
        let drawHeight = canvas.height;
        let offsetX = 0;
        let offsetY = 0;

        if (imgAspect > canvasAspect) {
          // Image is wider - fit to width
          drawHeight = canvas.width / imgAspect;
          offsetY = (canvas.height - drawHeight) / 2;
        } else {
          // Image is taller - fit to height
          drawWidth = canvas.height * imgAspect;
          offsetX = (canvas.width - drawWidth) / 2;
        }

        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        setHasSignature(true);
      };
      img.onerror = (err) => {
        console.error('Failed to load signature image:', err);
        setHasSignature(false);
      };
      img.src = initialSignature;
    } else {
      // Clear canvas if no initial signature
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasSignature(false);
    }
  }, [width, height, initialSignature]);

  const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    const { x, y } = getCoordinates(e);

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);

    ctx.lineTo(x, y);
    ctx.stroke();
    updateHasSignature();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const isCanvasEmpty = (): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return true;

    const ctx = canvas.getContext('2d');
    if (!ctx) return true;

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    // Check if there are any non-transparent pixels
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] !== 0) {
        return false; // Found a non-transparent pixel
      }
    }
    return true; // Canvas is empty
  };

  // Update hasSignature state based on canvas content
  const updateHasSignature = () => {
    setHasSignature(!isCanvasEmpty());
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    if (onClear) {
      onClear();
    }
  };

  // Check canvas content periodically to update button state
  useEffect(() => {
    const checkInterval = setInterval(() => {
      updateHasSignature();
    }, 200);
    return () => clearInterval(checkInterval);
  }, []);

  const save = async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error('Canvas not found');
      return;
    }

    if (isCanvasEmpty()) {
      console.warn('Cannot save empty signature');
      alert('Please draw a signature before saving.');
      return;
    }

    try {
      const dataUrl = canvas.toDataURL('image/png');
      console.log('Generated data URL, length:', dataUrl.length);
      if (!dataUrl || dataUrl === 'data:,') {
        console.error('Empty canvas data');
        return;
      }
      console.log('Calling onSave callback');
      await onSave(dataUrl);
    } catch (error) {
      console.error('Error saving signature:', error);
      alert('Error saving signature. Please try again.');
    }
  };

  return (
    <Box>
      <Paper
        elevation={1}
        sx={{
          border: '2px solid #ddd',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'white',
          position: 'relative'
        }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={(e) => {
            e.preventDefault();
            startDrawing(e);
          }}
          onTouchMove={(e) => {
            e.preventDefault();
            draw(e);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            stopDrawing();
          }}
          style={{
            cursor: 'crosshair',
            display: 'block',
            width: '100%',
            height: 'auto',
            touchAction: 'none',
            userSelect: 'none'
          }}
        />
      </Paper>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Clear />}
          onClick={clear}
          disabled={!hasSignature}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Save button clicked, hasSignature:', hasSignature);
            save();
          }}
          disabled={!hasSignature || saving}
        >
          {saving ? 'Saving...' : 'Save Signature'}
        </Button>
      </Box>
    </Box>
  );
};

export default SignaturePad;

